import { FloatingPortal } from '@floating-ui/react';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { RefObject, useEffect, useState } from 'react';

type FloatingRefreshButtonProps = {
  /** Number of new activities to display */
  count: number;
  /** Callback when button is clicked */
  onClick: () => void;
  /** Top offset in Tailwind spacing units (e.g., '8' for top-8) */
  topOffset?: string;
  /** Whether the button is visible */
  show?: boolean;
  /** Whether the button is in loading state (icon spins) */
  isLoading?: boolean;
  /** Container ref to center the button within (falls back to full screen if not provided) */
  containerRef?: RefObject<HTMLElement>;
};

/**
 * FloatingRefreshButton - A floating button that appears when new activities are available.
 * Uses FloatingPortal for proper z-index stacking and framer-motion for smooth animations.
 * When loading, the refresh icon spins to indicate data is being fetched.
 * When containerRef is provided, the button centers within that container (useful when outlet is open).
 */
export const FloatingRefreshButton = ({
  count,
  onClick,
  topOffset = '2rem',
  show,
  isLoading = false,
  containerRef,
}: FloatingRefreshButtonProps) => {
  const isVisible = show ?? count > 0;
  const [containerBounds, setContainerBounds] = useState<{
    left: number;
    width: number;
  } | null>(null);

  // Track container bounds for positioning when containerRef is provided
  // Effect needed because we need to read DOM measurements and update on resize/layout changes
  useEffect(() => {
    if (!containerRef?.current || !isVisible) {
      setContainerBounds(null);
      return;
    }

    const updateBounds = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerBounds({ left: rect.left, width: rect.width });
      }
    };

    // Initial measurement
    updateBounds();

    // Update on resize (handles outlet open/close which changes container width)
    const resizeObserver = new ResizeObserver(updateBounds);
    resizeObserver.observe(containerRef.current);

    // Also listen to window resize for viewport changes
    window.addEventListener('resize', updateBounds);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateBounds);
    };
  }, [containerRef, isVisible]);

  // Calculate positioning: if container bounds exist, center within container; otherwise center on screen
  const positionStyles = containerBounds
    ? {
        top: topOffset,
        left: containerBounds.left,
        width: containerBounds.width,
      }
    : { top: topOffset };

  return (
    <AnimatePresence>
      {isVisible && (
        <FloatingPortal>
          <motion.div
            className={classNames(
              'pointer-events-none fixed z-[10000] flex justify-center',
              !containerBounds && 'inset-x-0'
            )}
            style={positionStyles}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              type='button'
              className='pointer-events-auto flex items-center gap-2 rounded-full border border-grey-20 bg-white px-3 py-1 text-sm shadow-md hover:bg-grey-5 lg:text-base'
              onClick={onClick}
              disabled={isLoading}
              aria-label={
                isLoading
                  ? 'Loading new updates...'
                  : `${count} new updates available, click to refresh`
              }
              tabIndex={0}
            >
              <FontAwesomeIcon
                icon={faRotateRight}
                className={classNames(isLoading && 'animate-spin')}
              />
              <span>{isLoading ? 'Loading...' : `${count} new updates`}</span>
            </button>
          </motion.div>
        </FloatingPortal>
      )}
    </AnimatePresence>
  );
};

export default FloatingRefreshButton;
